import {
  type ComponentType,
  forwardRef,
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { Button, Group, NumberInput, Stack, Text } from '@mantine/core'
import { useCounter, useDisclosure } from '@mantine/hooks'

import { IconPlus } from '@repo/common/components/Icons'
import { TokenAndSymbolRender } from '@repo/common/components/TokenRender'
import {
  countFormatter,
  percentage_formatter,
  usdFormatter,
} from '@repo/common/helpers/formatters'

import { ActionHideShow } from '@/components/ActionHideShow'
import { ConnectWalletButton } from '@/components/ConnectWalletButton'
import { ContentBox } from '@/components/ContentBox'
import { WaitingMessageButton } from '@/components/WaitingMessageButton'
import { WorkflowModal } from '@/components/WorkflowModal'

import classes from './flow_helpers.module.css'

export { classes }

export function normalize_progress<T extends { progress: number }>(
  steps: T[],
): T[] {
  // normalize the progress math
  const steps_total = steps.reduce((acc, step) => acc + step.progress, 0)
  return steps.map((step) => ({
    ...step,
    progress: (step.progress * 100) / steps_total,
  }))
}

export type BasePanelProps = {
  onCompleted: () => void
  onCancel: () => void
}

export type StepsType<PanelProps extends BasePanelProps> = {
  title: string | (() => string)
  Panel: ComponentType<PanelProps>
  progress: number
  can_close?: boolean
  useSkipCheck: (args: Omit<PanelProps, 'onCompleted' | 'onCancel'>) => boolean
}[]

export function useFlowControl<PanelProps extends BasePanelProps>({
  steps,
  extra_args,
  final_action,
}: {
  steps: StepsType<PanelProps>
  extra_args: Omit<PanelProps, 'onCompleted' | 'onCancel'>
  final_action: () => Promise<void>
}) {
  const [active, { set: set_active }] = useCounter(0, {
    min: 0,
    max: steps.length - 1,
  })

  const [submitted, set_submitted] = useState(false)

  const onCancel = useCallback(() => {
    set_submitted(false)
  }, [])

  const skip_checks = steps.map((s) => s.useSkipCheck(extra_args))

  const titles = steps.map(({ title }) =>
    typeof title === 'function' ? title() : title,
  )

  const next_check = skip_checks.findIndex((val) => val === false)

  useEffect(() => {
    if (next_check != -1 && active != next_check) {
      set_active(next_check)
    }
  }, [active, next_check, set_active])

  const onPanelCompleted = useCallback(() => {
    set_active(next_check)
  }, [next_check, set_active])

  const progress = steps
    .slice(0, active)
    .reduce((acc, step) => acc + step.progress, 0)

  const all_checks_passed = skip_checks.reduce((acc, val) => acc && val, true)

  const onSubmit = () => {
    set_submitted(true)
  }

  let modal
  if (steps.length == 0) {
    modal = null
  } else {
    const Panel = steps[active].Panel
    const title = titles[active]

    modal = (
      <WorkflowModal
        progress={progress}
        opened={submitted && !all_checks_passed}
        onClose={onCancel}
        title={title}
        closeOnEscape
        closeOnClickOutside
        withCloseButton
      >
        <Panel
          {...(extra_args as PanelProps)}
          onCancel={onCancel}
          onCompleted={onPanelCompleted}
        />
      </WorkflowModal>
    )
  }

  useEffect(() => {
    if (submitted && all_checks_passed) {
      final_action().catch(console.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted && all_checks_passed])

  return {
    submitted,
    onSubmit,
    modal,
  }
}

export const FlowButton = memo<{
  wallet_isConnected: boolean
  form_valid: boolean
  is_loading: boolean
  paused: boolean | 'temporary'
  error_reason: string
  mutation_query: {
    isSuccess: boolean
    isPending: boolean
    isReady: boolean
  }
  action_word: string
  // eslint-disable-next-line max-lines-per-function, complexity
}>(function FlowButton({
  wallet_isConnected,
  form_valid,
  is_loading,
  paused,
  error_reason,
  mutation_query,
  action_word,
}) {
  if (paused || (wallet_isConnected && !is_loading && !form_valid)) {
    return (
      <>
        <WaitingMessageButton
          waiting_message="Confirm with wallet"
          disabled
          variant="outline"
          size="xl"
          fz={{
            base: error_reason.length > 28 ? 'md' : 'lg',
            md: error_reason.length > 36 ? 'lg' : 'xl',
          }}
        >
          {error_reason}
        </WaitingMessageButton>
        {paused == 'temporary' && (
          <Text ta="center">Checking again soon...</Text>
        )}
      </>
    )
  } else if (wallet_isConnected == false) {
    return <ConnectWalletButton size="xl" message="Connect Wallet First" />
  } else if (is_loading) {
    return (
      <WaitingMessageButton
        waiting_message="Loading"
        loading
        variant="outline"
        size="xl"
        fz={{ base: 'lg', md: 'var(--button-fz)' }}
      >
        Loading
      </WaitingMessageButton>
    )
  }
  return (
    <WaitingMessageButton
      waiting_message={
        mutation_query.isReady ? 'Confirm with wallet' : 'Preparing...'
      }
      disabled={mutation_query.isSuccess}
      loading={mutation_query.isPending || !mutation_query.isReady}
      variant="gradient"
      size="xl"
      type="submit"
    >
      {mutation_query.isSuccess ? 'Success' : action_word}
    </WaitingMessageButton>
  )
})

export const NumberRow = memo<
  ReactProps<typeof NumberInput> & {
    'aria-label': string
    symbol?: ReactProps<typeof TokenAndSymbolRender>['symbol'] | ReactElement
    balance_number?: number
    balance_maximumFractionDigits?: number
    max_button?: () => void
    value_number?: number
    details?: ReactNode
  } & (
      | {
          readonly: true
          value: number | string
        }
      | {
          readonly?: false
          value?: unknown
        }
    )
>(
  // eslint-disable-next-line max-lines-per-function, complexity
  forwardRef(function NumberRow(
    {
      readonly,
      value,
      symbol,
      balance_number,
      balance_maximumFractionDigits = 2,
      value_number,
      details,
      max_button,
      ...rest
    },
    ref,
  ) {
    let big_number
    if (readonly) {
      big_number = (
        <Text size="xl1" lh="1" opacity={0.7} title={rest['aria-label']}>
          {countFormatter(Number(value), {
            maximumFractionDigits: 2,
            compact: false,
          })}
        </Text>
      )
    } else {
      big_number = (
        <NumberInput
          variant="unstyled"
          allowNegative={false}
          decimalScale={2}
          hideControls
          placeholder="0"
          flex="1 1 100px"
          value={value}
          classNames={{ input: classes.input }}
          ref={ref}
          {...rest}
        />
      )
    }

    return (
      <Stack gap="0">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          {big_number}
          {typeof symbol == 'string' ? (
            <TokenAndSymbolRender symbol={symbol} size="lg" />
          ) : (
            <>{symbol}</>
          )}
        </Group>
        {(details != null ||
          balance_number != null ||
          value_number != null) && (
          <Group justify="space-between" align="baseline" gap="0">
            {details != null && (
              <Text size="xs" opacity={0.7}>
                {details}
              </Text>
            )}
            {value_number != null ? (
              <Text size="md" opacity={0.7}>
                value: {usdFormatter(value_number)}
              </Text>
            ) : (
              <div />
            )}
            {balance_number != null && (
              <Group justify="flex-end" align="baseline" gap="0">
                <Text size="md" opacity={0.7}>
                  balance:{' '}
                  {countFormatter(balance_number, {
                    compact: false,
                    maximumFractionDigits: balance_maximumFractionDigits,
                  })}
                </Text>
                {max_button != null && (
                  <Button
                    size="compact-md"
                    variant="subtle"
                    disabled={rest.disabled}
                    onClick={max_button}
                  >
                    MAX
                  </Button>
                )}
              </Group>
            )}
          </Group>
        )}
      </Stack>
    )
  }),
)

export const RatioAndDisclosures = memo<{
  symbol?: string
  price?: number | null
  maximumFractionDigits?: number
  children: ReactNode
}>(function RatioAndDisclosures({
  symbol,
  price,
  maximumFractionDigits = 2,
  children,
}) {
  const [show_details, show_details_actions] = useDisclosure(false)

  return (
    <>
      <Group pl="sm" justify="space-between">
        {price ? (
          <Text size="md" opacity={0.7}>
            1 {symbol} ={' '}
            {countFormatter(price ?? 0, {
              compact: false,
              maximumFractionDigits: maximumFractionDigits ?? 2,
            })}{' '}
            USDC
          </Text>
        ) : (
          <div />
        )}
        <ActionHideShow
          toggle={show_details_actions.toggle}
          opened={show_details}
          item_noun="buy disclosures"
        />
      </Group>
      {show_details && (
        <Stack gap="xs" px="sm" opacity={0.7}>
          <Text>Disclosures:</Text>
          {children}
        </Stack>
      )}
    </>
  )
})

export const RewardsRowBox = memo<{
  message: string
  percentage: number
  tokens: number
  token_value: number
}>(function RewardsRowBox({ message, percentage, tokens, token_value }) {
  return (
    <ContentBox p="lg" variant="fading" gap="xs" data-testid="FISN_container">
      <Group gap="0.5ex">
        <Text size="md" lh="1" opacity={0.9}>
          {message}
        </Text>
        <Group c="green.2" gap="0">
          <IconPlus size="1.2em" />
          <Text size="md" lh="1">
            {percentage_formatter(percentage)}
          </Text>
        </Group>
      </Group>
      <NumberRow
        readonly
        value={tokens}
        symbol="FISN"
        aria-label="FISN rewards tokens"
        value_number={token_value}
      />
    </ContentBox>
  )
})
